import { getAppData } from "./getAppData"
import JSZip from "jszip"
import { getPageFromTargets } from "./getPageIndexFromSb3";

const electron = (window as any).electron;
const fs = electron?.fs;
const path = electron?.path;

export interface AppInfo {
    // app.json
    id: string
    name: {[key: string]: string}
    description: {[key: string]: string}
    detailDescription: {[key: string]: string}
    cover: string
    dynamicCover?: string // 动态封面图
    version: string
    platformApiVersion: string // 构建应用时的平台API版本, 用于平台判断是否能加载此应用
    category: "coding"|"model"|"ui"
    openUrl?: { // 页面url, 如无, 只打开后端程序, 不加载页面
        type: "inner-web"|"external-web" // inner-web内部标签页打开网页, external-web默认浏览器打开网页
        src: string // https://mindplus.cc
    }|{
        type: "inner-auto-port" // express自动分配端口
        target: string // 资源路径, {realtime-gui}
    }|{
        type: "inner-config" // 从config读取配置
        src: string // http://localhost:[httpPort]
        config: string // {ai-tools-server}/config.json
    }
    config?: string // 配置之后, 可在启动平台右键菜单修改应用的配置
    beforeOpen?: { // 在打开页面之前, 需要做的事情
        type: "cmd"
        command: string // {mind-link}/start.bat
        processId?: string // 进程得唯一id, 避免启动多个进程, 如果为空, 每次打开页面都会运行一次command
        readySignal?: string // 如果配置了该项, 匹配到后端程序的打印之后, 才会加载页面
    }
    deps?: {
        [id: string]: {
            version: string
            downloadUrl: string
        }
    }
    fileExtension: string[] // 文件后缀名
    // 状态
    status: 0|1|2 // 0: 已下载, 1: 未下载, 2: 下载中, 3: 下载失败
    hasUpdate: boolean // 是否有更新
    isBuiltin: boolean // 是否在安装目录下
    canUse: boolean // 是否可以使用
    disableMultiple?: boolean // 禁用多开
}

// 项目文件中的config.json
interface ProjectConfigJson {
    appInfo: {
        id: string
        version: string
    }
}

const STALE_TIME = 30; // 数据过期时间, 秒

class AppManager {
    _apps: AppInfo[] = []
    appsRefreshTime: number = 0 // 数据刷新时间
    refreshCallback: ((data: AppInfo[]) => void)|null = null
    appsRefreshPromise: Promise<any> | null = null
    specialExt: string[] = [".sb3", ".mp", ".mpml"]

    refreshAppList(callback?: (data: AppInfo[]) => void) {
        // 先用旧数据刷新一下
        if (callback) callback(this._apps);
        // 数据未过期
        if (Date.now() - this.appsRefreshTime < STALE_TIME * 1000) {
            return Promise.resolve(this._apps);
        }
        // 数据过期
        if (callback) this.refreshCallback = callback;
        // 1. 如果正在获取
        if (this.appsRefreshPromise) return this.appsRefreshPromise;
        // 如果数据过期, 重新获取
        this.appsRefreshPromise = getAppData((data) => {
            if (this.refreshCallback) this.refreshCallback(data);
        }).then((apps) => {
            this.refreshCallback = null;
            // 缓存数据
            if (apps) this._apps = apps;
            // 记录刷新时间
            this.appsRefreshTime = Date.now();
            // 清除promise
            this.appsRefreshPromise = null;
            return apps;
        })
        return this.appsRefreshPromise
    }

    async getAppInfoById(appId: string) {
        const apps = await this.refreshAppList()
        const ret = apps.find((item: AppInfo) => item.id === appId)
        return ret;
    }

    // 通过后缀名查询app
    async getAppInfoByFileExtension(ext: string, projectPath: string) {
        if (this.isSpecialFileExtention(ext)) {
            return this.getAppInfoBySpecialExt(ext, projectPath);
        }

        // 如果fs不可用，返回undefined
        if (!fs) {
            return undefined;
        }

        // 1. 解压项目文件
        const data = await fs.readFile(projectPath).catch((e: any) => null);
        if (!data) return;
        const zip = await JSZip.loadAsync(data);
        if (!zip.files['config.json']) return;
        // 2. 读取config.json
        const content = await zip.files['config.json'].async('string');
        let appId = "";
        try {
            // 3. 获取app id
            const json: ProjectConfigJson = JSON.parse(content);
            appId = json.appInfo?.id;
        } catch {
            return;
        }
        if (!appId) return;
        // 4. 获取appInfo
        const ret = await this.getAppInfoById(appId)
        return ret;
    }

    // 是否是特殊的后缀名
    isSpecialFileExtention(ext: string) {
        return this.specialExt.indexOf(ext) !== -1;
    }

    async getAppInfoBySpecialExt(ext: string, projectPath: string) {
        console.log("getAppInfoBySpecialExt", ext, projectPath)
        let appId = "";
        
        // 如果fs不可用，返回undefined
        if (!fs) {
            return undefined;
        }
        
        if (ext === ".mpml") {
            // todo: 解析mpml文件, 得到appId
            const data = await fs.readFile(projectPath).catch((e: any) => null);
            if (!data) return;
            const zip = await JSZip.loadAsync(data);
            if (!zip.files['manifest.json']) return;
            const content = await zip.files['manifest.json'].async('string');
            try {
                const json = JSON.parse(content);
                appId = `ai-tools-${json["type"]}`
                return this.getAppInfoById(appId)
            } catch {
            }
        }
        if (ext === ".sb3" || ext === ".mp") {
            const data = await fs.readFile(projectPath).catch((e: any) => null);
            if (!data) return;
            const zip = await JSZip.loadAsync(data);
            if (!zip.files['project.json']) return;
            const content = await zip.files['project.json'].async('string');
            try {
                const json = JSON.parse(content);
                console.log("project.json", json, typeof json.pageIndex === "number")
                if (typeof json.pageIndex === "number") {
                    if (json.pageIndex === 0) {
                        appId = "realtime"
                    }
                    if (json.pageIndex === 1) {
                        if (json.language === "micropython") {
                            appId = "micropython-block"
                        } if (json["page1"]?.pageInfo?.codeLanguage === "micropython") {
                            appId = "micropython-block"
                        }else {
                            appId = "upload"
                        }
                    }
                    if (json.pageIndex === 2) {
                        appId = "python-block"
                    }
                } else if (json.pageInfo) {
                    if (json.pageInfo.page === 0) {
                        appId = "realtime"
                    }
                    if (json.pageInfo.page === 1) {
                        if (json.pageInfo.codeLanguage === "micropython") {
                            appId = "micropython-block"
                        } else {
                            appId = "upload"
                        }
                    }
                    if (json.pageInfo.page === 2) {
                        appId = "python-block"
                    }
                } else {
                    // 旧版某些sb3只保存了当前模式的内容, 且没有页面标志, 只能靠block来区分, 例如: 示例程序
                    const index = getPageFromTargets(json)
                    if (index === 0) appId = "realtime";
                    if (index === 1) appId = "upload";
                    // scratch官方的项目
                    if (index === null && ext === ".sb3") {
                        appId = "realtime";
                    }
                }
            }catch{}
        }
        console.log("getAppInfoBySpecialExt", ext, appId)
        if (appId) {
            return this.getAppInfoById(appId)
        } else {
            return Promise.reject()
        }
    }
}

export const appManager = new AppManager();
